// ============================================================
// ✅ MainLayout.jsx - Layout principal de l'application
// Sidebar + Header + Contenu principal
// Support : User, Manager, Admin, SuperAdmin, Observateur
// Spécial client : Manager = même menu que Admin
// Observateur : rôle dédié = planning uniquement (Accueil)
// ============================================================

import { useState, useEffect } from 'react';
import {
  Home, Groups, CorporateFare, Hub, Engineering,
  EventNote, QueryStats, CheckCircle,
  Logout, AccountCircle, BeachAccess,
  CalendarMonth, AccountBalanceWallet, TrendingUp,
  Business, Public, Settings, Map, Gavel
} from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

export default function MainLayout({ children, user }) {
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [userService, setUserService] = useState('Chargement...');
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

  // ✅ Détection des rôles
  const isSuperAdmin = user?.EstSuperAdmin === 1 || user?.EstSuperAdmin === true;
  const isObservateur = user?.Role === 'observateur';  // ✅ Rôle dédié

  // ==========================================================
  // 📱 DÉTECTION MOBILE
  // ==========================================================
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) setSidebarOpen(false);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // ==========================================================
  // 📋 MENU COMPLET
  // Rôles : user | manager | admin | superadmin | observateur
  // Spécial : manager voit le même menu que admin (demande client)
  // Observateur : menu réduit (Accueil uniquement)
  // ==========================================================
  const getMenuItems = (userData) => {
    
    const role = userData?.Role;
    const estSuperAdmin = userData?.EstSuperAdmin === 1 || userData?.EstSuperAdmin === true;

    // Tous les items avec leur ordre, visibilité et séparateur
    const allItems = [
      // ── ACCUEIL & ANALYSE ──
      // ✅ Observateur voit l'Accueil uniquement
      { label: 'Accueil',       icon: <Home />,              path: '/menu',        roles: ['user', 'manager', 'admin', 'superadmin', 'observateur'], order: 1 },
      { label: 'Analyse',       icon: <QueryStats />,        path: '/bi',          roles: ['manager', 'admin', 'superadmin'],         order: 2, separatorAfter: true },
      
      // ── PAYS & MAP (SuperAdmin uniquement) ──
      { label: 'Pays',          icon: <Public />,            path: '/pays',        roles: ['superadmin'],                            order: 3 },
      { label: 'Map et Loi',    icon: <Map />,               path: '/map-loi',     roles: ['superadmin'],                            order: 4, separatorAfter: true },
      
      // ── SOCIÉTÉ (SuperAdmin uniquement) ──
      { label: 'Société',       icon: <Business />,          path: '/societes',    roles: ['superadmin'],                            order: 5, separatorAfter: true },
      
      // ── STRUCTURE (Admin + Manager + SuperAdmin) ──
      { label: 'Départements',  icon: <CorporateFare />,     path: '/departements', roles: ['manager', 'admin', 'superadmin'],        order: 6 },
      { label: 'Services',      icon: <Hub />,               path: '/services',    roles: ['manager', 'admin', 'superadmin'],        order: 7 },
      { label: 'Postes',        icon: <Engineering />,       path: '/postes',      roles: ['manager', 'admin', 'superadmin'],        order: 8, separatorAfter: true },
      
      // ── EMPLOYÉS (Admin + Manager + SuperAdmin) ──
      { label: 'Employés',      icon: <Groups />,            path: '/employes',    roles: ['manager', 'admin', 'superadmin'],        order: 9, separatorAfter: true },
      
      // ── CONGÉS (Admin + Manager + SuperAdmin) ──
      { label: 'Types de congés', icon: <BeachAccess />,     path: '/typesconge',  roles: ['manager', 'admin', 'superadmin'],        order: 10 },
      { label: 'Jours fériés',  icon: <CalendarMonth />,     path: '/feries',      roles: ['manager', 'admin', 'superadmin'],        order: 11, separatorAfter: true },
      
      // ── DEMANDES ──
      // ✅ User, Manager, Admin, SuperAdmin (pas observateur)
      { label: 'Mes demandes',  icon: <EventNote />,         path: '/demandes',    roles: ['user', 'manager', 'admin', 'superadmin'], order: 12 },
      // ✅ Manager, Admin, SuperAdmin
      { label: 'Validations',   icon: <CheckCircle />,       path: '/validations', roles: ['manager', 'admin', 'superadmin'],        order: 13, separatorAfter: true },
      
      // ── SOLDES & FIN ANNÉE (Admin + Manager + SuperAdmin) ──
      { label: 'Remplissage Solde',      icon: <AccountBalanceWallet />, path: '/remplissage-soldes',   roles: ['manager', 'admin', 'superadmin'], order: 14 },
      { label: 'Traitement Fin d\'année', icon: <TrendingUp />,          path: '/traitement-fin-annee', roles: ['manager', 'admin', 'superadmin'], order: 15, separatorAfter: true },
      
      // ✅ Saisie rétroactive (Admin + Manager + SuperAdmin)
      { label: 'Saisie rétroactive',      icon: <CalendarMonth />,       path: '/saisie-retroactive',   roles: ['manager', 'admin', 'superadmin'], order: 15.6, separatorAfter: true },
      
      // ── PARAMÉTRAGE (Admin + Manager + SuperAdmin) ──
      { label: 'Paramétrage des règles', icon: <Settings />,  path: '/parametrage', roles: ['manager', 'admin', 'superadmin'],        order: 16 },
    ];

    // Mapper le rôle utilisateur
    const userRole = estSuperAdmin ? 'superadmin' : (role || 'user');

    // Filtrer les items visibles selon le rôle ET la condition
    const visibleItems = allItems.filter(item => {
      if (!item.roles.includes(userRole)) return false;
      if (item.condition && !item.condition(userData)) return false;
      return true;
    });

    // Trier par ordre
    return visibleItems.sort((a, b) => a.order - b.order);
  };

  const menuItems = getMenuItems(user);

  // ==========================================================
  // 🏢 RÉCUPÉRATION DU SERVICE DE L'UTILISATEUR
  // ==========================================================
  useEffect(() => {
    const fetchUserService = async () => {
      if (!user?.id) return;
      try {
        const res = await axios.get(`${API_URL}/employes/${user.id}`);
        if (res.data.success && res.data.employe) {
          const employe = res.data.employe;
          if (employe.ID_Postes) {
            const posteRes = await axios.get(`${API_URL}/postes/${employe.ID_Postes}`);
            if (posteRes.data.success && posteRes.data.poste?.ID_Services) {
              const serviceRes = await axios.get(`${API_URL}/services/${posteRes.data.poste.ID_Services}`);
              if (serviceRes.data.success && serviceRes.data.service) {
                setUserService(serviceRes.data.service.Nom_Services || serviceRes.data.service.Nom_Service);
                return;
              }
            }
          }
        }
        setUserService('Non défini');
      } catch (err) {
        setUserService('RH');
      }
    };
    fetchUserService();
  }, [user, API_URL]);

  // ==========================================================
  // 🔄 TOGGLE SIDEBAR
  // ==========================================================
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // ==========================================================
  // 🎨 COULEUR PAR ICÔNE
  // ==========================================================
  const getIconColor = (label) => {
    const map = {
      'Accueil': 'text-emerald-300',
      'Analyse': 'text-purple-300',
      'Pays': 'text-blue-300',
      'Map et Loi': 'text-amber-300',
      'Société': 'text-purple-300',
      'Départements': 'text-amber-300',
      'Services': 'text-teal-300',
      'Postes': 'text-indigo-300',
      'Employés': 'text-cyan-300',
      'Types de congés': 'text-yellow-300',
      'Jours fériés': 'text-orange-300',
      'Mes demandes': 'text-blue-300',
      'Validations': 'text-green-300',
      'Remplissage Solde': 'text-green-300',
      "Traitement Fin d'année": 'text-red-300',
      'Saisie rétroactive': 'text-amber-300',
      'Paramétrage des règles': 'text-purple-300',
    };
    return map[label] || 'text-gray-300';
  };

  // ==========================================================
  // 🎨 RENDU
  // ==========================================================
  return (
    <div className="flex h-screen bg-gray-50 font-sans text-sm text-gray-800">
      
      {/* ==================================================== */}
      {/* SIDEBAR - Navigation principale                        */}
      {/* ==================================================== */}
      <div className={`flex flex-col bg-emerald-800 text-white transition-all duration-300 ease-in-out ${sidebarOpen ? 'w-52' : 'w-14'} relative z-20 shadow-lg`}>
        
        {/* Logo / Titre */}
        <div className="flex items-center justify-center py-2 border-b border-emerald-700">
          {sidebarOpen ? (
            <span className="text-sm font-semibold">
              {isSuperAdmin ? '🌍 GestCongés' : (user?.SocieteSigle || 'GestCongés')}
            </span>
          ) : (
            <span className="text-sm font-bold">
              {isSuperAdmin ? '🌍' : (user?.SocieteSigle?.charAt(0) || 'G')}
            </span>
          )}
          <button onClick={toggleSidebar} className="absolute right-1 text-white hover:bg-emerald-700 rounded p-0.5">
            {sidebarOpen ? '◀' : '▶'}
          </button>
        </div>

        {/* Menu de navigation */}
        <nav className="flex-1 overflow-y-auto px-1 py-2 custom-scrollbar">
          {menuItems.map((item, idx) => (
            <div key={idx}>
              <Tooltip title={!sidebarOpen ? item.label : ''} placement="right" arrow>
                <button
                  onClick={() => navigate(item.path)}
                  className={`flex items-center gap-2 w-full px-2 py-1.5 my-0.5 rounded-md transition text-white hover:bg-emerald-700 ${sidebarOpen ? 'justify-start' : 'justify-center'}`}
                >
                  <span className={`text-base ${getIconColor(item.label)}`}>{item.icon}</span>
                  {sidebarOpen && <span className="text-[11px] font-medium whitespace-nowrap">{item.label}</span>}
                </button>
              </Tooltip>
              
              {/* Séparateur visuel entre les sections */}
              {item.separatorAfter && (
                <div className="my-1 mx-2 border-t border-emerald-700/50" />
              )}
            </div>
          ))}
        </nav>

        {/* Bouton Déconnexion */}
        <div className="px-2 py-2 border-t border-emerald-700">
          <button
            onClick={() => navigate('/')}
            className={`flex items-center gap-2 text-red-200 hover:bg-red-800 hover:text-white w-full px-2 py-1.5 rounded-md transition ${sidebarOpen ? 'justify-start' : 'justify-center'}`}
          >
            <Logout className="text-base" />
            {sidebarOpen && <span className="text-[11px] font-medium">Déconnexion</span>}
          </button>
        </div>
      </div>

      {/* Scrollbar verte personnalisée */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #065f46; border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #34d399; border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #6ee7b7; }
      `}</style>

      {/* Overlay mobile */}
      {isMobile && sidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-10" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ==================================================== */}
      {/* CONTENU PRINCIPAL                                     */}
      {/* ==================================================== */}
      <div className="flex-1 flex flex-col overflow-y-auto">
        
        {/* Header */}
        <header className="flex items-center justify-between px-4 py-2 bg-white border-b shadow-sm sticky top-0 z-10">
          
          {/* Bouton burger mobile */}
          {isMobile && (
            <button onClick={toggleSidebar} className="p-1 rounded text-gray-600 hover:bg-gray-100">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}

          {/* Titre application */}
          <div>
            <h1 className="text-base md:text-lg font-bold text-emerald-700">
              {isSuperAdmin ? '🌍 GestCongés' : (user?.SocieteNom || 'GestCongés')}
            </h1>
          </div>

          {/* Profil utilisateur */}
          <div className="flex items-center gap-2">
            <div className="text-right">
              <div className="text-xs font-medium text-emerald-700">
                {user?.Prenom} {user?.Nom}
                {/* Badge selon le rôle */}
                {isSuperAdmin && (
                  <span className="ml-1 text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">👑</span>
                )}
                {isObservateur && (
                  <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">👁️</span>
                )}
              </div>
              <div className="text-[10px] text-gray-500 hidden sm:block">
                {isSuperAdmin ? '👑 SuperAdmin' : 
                 isObservateur ? '👁️ Observateur' : 
                 `${userService} ${user?.SocieteSigle ? '• ' + user.SocieteSigle : ''}`}
              </div>
            </div>
            <AccountCircle className="text-emerald-600 text-2xl" />
          </div>
        </header>

        {/* Zone de contenu principal */}
        <main className="p-3 md:p-5 bg-gray-50 flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}